import { useEffect, useRef } from 'react';
import type { Location } from './types';
import { getRomaniaCenter, isGoogleMapsLoaded } from './utils';

const win = typeof window !== 'undefined' ? (window as { google?: { maps: { Map: new (el: HTMLElement, o?: object) => unknown; importLibrary: (l: string) => Promise<unknown> } } }) : null;

export function useLocationPickerMap(isOpen: boolean, selectedLocations: Location[]) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const markersRef = useRef<unknown[]>([]);

  useEffect(() => {
    if (!isOpen || !mapRef.current || !isGoogleMapsLoaded() || mapInstanceRef.current || !win?.google) return;
    const map = new win.google.maps.Map(mapRef.current, {
      center: getRomaniaCenter(),
      zoom: 6,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapId: 'LOCATION_PICKER_MAP',
    });
    mapInstanceRef.current = map;
    return () => {
      (markersRef.current as { map?: unknown }[]).forEach((m) => { m.map = null; });
      markersRef.current = [];
      mapInstanceRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    const map = mapInstanceRef.current as { setCenter: (c: object) => void; setZoom: (z: number) => void; fitBounds: (b: unknown) => void } | null;
    const google = win?.google;
    if (!map || !isGoogleMapsLoaded() || !google) return;
    (markersRef.current as { map?: unknown }[]).forEach((m) => { m.map = null; });
    markersRef.current = [];

    if (selectedLocations.length === 0) {
      map.setCenter(getRomaniaCenter());
      map.setZoom(6);
      return;
    }

    const createMarker = async (loc: Location) => {
      const lib = await google.maps.importLibrary('marker') as { AdvancedMarkerElement: new (o: object) => unknown };
      return new lib.AdvancedMarkerElement({
        map,
        position: { lat: loc.latitude, lng: loc.longitude },
        title: loc.name,
      });
    };

    if (selectedLocations.length === 1) {
      const loc = selectedLocations[0];
      map.setCenter({ lat: loc.latitude, lng: loc.longitude });
      map.setZoom(10);
      createMarker(loc).then((m) => markersRef.current.push(m));
    } else {
      (async () => {
        const core = await google.maps.importLibrary('core') as { LatLngBounds: new () => { extend: (p: object) => void } };
        const bounds = new core.LatLngBounds();
        const markerPromises = selectedLocations.map(async (loc) => {
          bounds.extend({ lat: loc.latitude, lng: loc.longitude });
          return createMarker(loc);
        });
        const markers = await Promise.all(markerPromises);
        markersRef.current.push(...markers);
        map.fitBounds(bounds);
      })();
    }
  }, [selectedLocations]);

  return mapRef;
}
